import { useEffect, useState, useCallback } from "react";
import { Loader2, Plus, Trash2, Download, Wallet, TrendingUp, TrendingDown } from "lucide-react";
import { api, formatApiError } from "../../lib/api";
import { fcfa } from "../../lib/format";
import { toast } from "sonner";

const CATEGORIES = [
  { id: "achat_stock", label: "Achat de stock" },
  { id: "transport", label: "Transport/Livraison" },
  { id: "marketing", label: "Marketing/Pub" },
  { id: "salaires", label: "Salaires" },
  { id: "loyer", label: "Loyer" },
  { id: "communication", label: "Communication" },
  { id: "frais_bancaires", label: "Frais bancaires" },
  { id: "autre", label: "Autre" },
];
const CAT_LABEL = Object.fromEntries(CATEGORIES.map((c) => [c.id, c.label]));
const PAY_METHODS = ["Cash", "Moov (Flooz)", "Togocel (T-Money)", "Virement", "Autre"];
const MONTHS = ["Jan", "Fév", "Mar", "Avr", "Mai", "Juin", "Juil", "Août", "Sep", "Oct", "Nov", "Déc"];

const today = () => new Date().toISOString().slice(0, 10);

export default function Expenses() {
  const [year, setYear] = useState(String(new Date().getFullYear()));
  const [expenses, setExpenses] = useState(null);
  const [summary, setSummary] = useState(null);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({ date: today(), amount: "", category: "achat_stock", description: "", supplier: "", payment_method: "Cash" });

  const inputCls = "w-full border border-brand-line rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-brand/30";

  const load = useCallback(() => {
    api.get(`/admin/expenses?year=${year}`).then(({ data }) => setExpenses(data)).catch(() => setExpenses([]));
    api.get(`/admin/expenses/summary?year=${year}`).then(({ data }) => setSummary(data)).catch(() => setSummary(null));
  }, [year]);
  useEffect(() => { load(); }, [load]);

  const add = async () => {
    if (!Number(form.amount) || Number(form.amount) <= 0) return toast.error("Montant invalide");
    if (!form.date) return toast.error("Choisissez une date");
    setSaving(true);
    try {
      await api.post("/admin/expenses", { ...form, amount: Number(form.amount) });
      toast.success("Dépense enregistrée");
      setForm({ date: today(), amount: "", category: "achat_stock", description: "", supplier: "", payment_method: "Cash" });
      load();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  const remove = async (id) => {
    if (!window.confirm("Supprimer cette dépense ?")) return;
    try { await api.delete(`/admin/expenses/${id}`); toast.success("Supprimée"); load(); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  const exportCsv = async () => {
    try {
      const res = await api.get(`/admin/expenses/export?year=${year}`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement("a");
      a.href = url; a.download = `depenses_noumabou_${year}.csv`;
      document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(url);
    } catch { toast.error("Export impossible"); }
  };

  const years = [];
  for (let y = new Date().getFullYear(); y >= 2024; y--) years.push(String(y));
  const maxMonth = summary ? Math.max(...Object.values(summary.by_month), 1) : 1;

  return (
    <div className="space-y-6" data-testid="expenses-page">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="font-heading text-3xl font-black tracking-tight text-brand-ink">Dépenses</h1>
          <p className="text-brand-muted mt-1">Enregistrez vos achats et dépenses pour la comptabilité.</p>
        </div>
        <div className="flex items-center gap-2">
          <select value={year} onChange={(e) => setYear(e.target.value)} data-testid="expense-year-select" className="border border-brand-line rounded-lg px-3 py-2 text-sm">
            {years.map((y) => <option key={y} value={y}>{y}</option>)}
          </select>
          <button onClick={exportCsv} data-testid="export-expenses-button" className="bg-white border border-brand-line text-brand-ink hover:border-brand rounded-full px-4 py-2 text-sm font-semibold flex items-center gap-2">
            <Download className="w-4 h-4" /> Exporter (Excel)
          </button>
        </div>
      </div>

      {/* Résumé */}
      {summary && (
        <div className="grid sm:grid-cols-3 gap-3">
          <div className="bg-white border border-brand-line rounded-2xl p-5">
            <p className="text-xs text-brand-muted flex items-center gap-1"><TrendingUp className="w-3.5 h-3.5 text-green-600" /> Chiffre d'affaires {year}</p>
            <p className="font-heading font-black text-2xl text-brand-ink mt-1">{fcfa(summary.revenue)}</p>
          </div>
          <div className="bg-white border border-brand-line rounded-2xl p-5">
            <p className="text-xs text-brand-muted flex items-center gap-1"><TrendingDown className="w-3.5 h-3.5 text-red-500" /> Dépenses {year}</p>
            <p className="font-heading font-black text-2xl text-brand-ink mt-1">{fcfa(summary.total_expenses)}</p>
          </div>
          <div className={`rounded-2xl p-5 border ${summary.net >= 0 ? "bg-green-50 border-green-200" : "bg-red-50 border-red-200"}`} data-testid="net-profit">
            <p className="text-xs flex items-center gap-1"><Wallet className="w-3.5 h-3.5" /> Bénéfice net {year}</p>
            <p className={`font-heading font-black text-2xl mt-1 ${summary.net >= 0 ? "text-green-800" : "text-red-700"}`}>{fcfa(summary.net)}</p>
          </div>
        </div>
      )}

      {/* Ajouter une dépense */}
      <div className="bg-white border border-brand-line rounded-2xl p-5 sm:p-6">
        <h2 className="font-heading font-bold text-brand-ink mb-4 flex items-center gap-2"><Plus className="w-5 h-5 text-brand" /> Nouvelle dépense</h2>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
          <input type="date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} className={inputCls} data-testid="expense-date" />
          <input type="number" value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })} placeholder="Montant (FCFA)" className={inputCls} data-testid="expense-amount" />
          <select value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} className={inputCls} data-testid="expense-category">
            {CATEGORIES.map((c) => <option key={c.id} value={c.id}>{c.label}</option>)}
          </select>
          <input value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} placeholder="Description" className={inputCls} data-testid="expense-description" />
          <input value={form.supplier} onChange={(e) => setForm({ ...form, supplier: e.target.value })} placeholder="Fournisseur (optionnel)" className={inputCls} data-testid="expense-supplier" />
          <select value={form.payment_method} onChange={(e) => setForm({ ...form, payment_method: e.target.value })} className={inputCls} data-testid="expense-payment">
            {PAY_METHODS.map((p) => <option key={p} value={p}>{p}</option>)}
          </select>
        </div>
        <button onClick={add} disabled={saving} data-testid="add-expense-button" className="mt-4 bg-brand text-white hover:bg-brand-hover rounded-full px-6 py-3 font-semibold flex items-center gap-2 disabled:opacity-60">
          {saving ? <Loader2 className="w-5 h-5 animate-spin" /> : <Plus className="w-5 h-5" />} Enregistrer la dépense
        </button>
      </div>

      {/* Répartition par catégorie + par mois */}
      {summary && summary.total_expenses > 0 && (
        <div className="grid md:grid-cols-2 gap-4">
          <div className="bg-white border border-brand-line rounded-2xl p-5">
            <h3 className="font-heading font-bold text-brand-ink mb-3">Par catégorie</h3>
            {summary.by_category.map((c) => (
              <div key={c.category} className="flex items-center justify-between py-1.5 text-sm">
                <span className="text-brand-sub">{c.label}</span>
                <span className="font-semibold text-brand-ink">{fcfa(c.amount)}</span>
              </div>
            ))}
          </div>
          <div className="bg-white border border-brand-line rounded-2xl p-5">
            <h3 className="font-heading font-bold text-brand-ink mb-3">Par mois</h3>
            <div className="space-y-1.5">
              {Object.entries(summary.by_month).map(([m, v]) => (
                <div key={m} className="flex items-center gap-2 text-xs">
                  <span className="w-8 text-brand-muted">{MONTHS[parseInt(m) - 1]}</span>
                  <div className="flex-1 bg-brand-cream rounded-full h-3 overflow-hidden">
                    <div className="bg-brand h-full rounded-full" style={{ width: `${(v / maxMonth) * 100}%` }} />
                  </div>
                  <span className="w-24 text-right font-medium text-brand-ink">{fcfa(v)}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Liste */}
      <div className="bg-white border border-brand-line rounded-2xl p-5 sm:p-6">
        <h2 className="font-heading font-bold text-brand-ink mb-4">Dépenses {year}</h2>
        {expenses === null ? <div className="flex justify-center py-8"><Loader2 className="w-6 h-6 animate-spin text-brand" /></div>
          : expenses.length === 0 ? <p className="text-sm text-brand-muted">Aucune dépense enregistrée pour {year}.</p>
          : (
            <div className="space-y-2">
              {expenses.map((e) => (
                <div key={e.id} data-testid={`expense-row-${e.id}`} className="flex items-center justify-between border border-brand-line rounded-xl px-4 py-3">
                  <div>
                    <p className="font-semibold text-brand-ink text-sm">{fcfa(e.amount)} <span className="text-brand-muted font-normal">· {CAT_LABEL[e.category] || e.category}</span></p>
                    <p className="text-xs text-brand-muted">{new Date(e.date).toLocaleDateString("fr-FR")}{e.description ? ` · ${e.description}` : ""}{e.supplier ? ` · ${e.supplier}` : ""}</p>
                  </div>
                  <button onClick={() => remove(e.id)} data-testid={`delete-expense-${e.id}`} className="text-red-500 hover:bg-red-50 rounded-lg p-2"><Trash2 className="w-4 h-4" /></button>
                </div>
              ))}
            </div>
          )}
      </div>
    </div>
  );
}
